import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { useAuth } from '@/lib/auth/contexto';
import { mensagemDeErro } from '@/lib/erros';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Marca } from '@/ui/shell/marca';
import { senhaSchema } from '@fiscoai/contracts';

import { trocarSenha } from './api';
import { CamposTrocaSenha } from './campos-troca-senha';

const formularioSchema = z
  .object({
    senhaAtual: z.string().min(1, 'Informe a senha atual'),
    novaSenha: senhaSchema,
    confirmacao: z.string(),
  })
  .refine((f) => f.novaSenha !== f.senhaAtual, {
    message: 'A nova senha precisa ser diferente da atual',
    path: ['novaSenha'],
  })
  .refine((f) => f.confirmacao === f.novaSenha, {
    message: 'As duas senhas não são iguais',
    path: ['confirmacao'],
  });
type Formulario = z.infer<typeof formularioSchema>;

/**
 * Troca da própria senha. Com a senha definida por outra pessoa (troca pendente), é a única tela
 * que abre: a pessoa escolhe a própria senha antes de usar o sistema.
 */
export function PaginaSenha({ nomeProduto }: { nomeProduto: string }) {
  const { usuario, entrar, sair } = useAuth();
  const navegar = useNavigate();
  const pendente = usuario?.trocaSenhaPendente === true;
  const formulario = useForm<Formulario>({
    resolver: zodResolver(formularioSchema),
    defaultValues: { senhaAtual: '', novaSenha: '', confirmacao: '' },
  });
  const troca = useMutation({
    mutationFn: ({ senhaAtual, novaSenha }: Formulario) => trocarSenha({ senhaAtual, novaSenha }),
    gcTime: 0,
    onSuccess: async (sessao) => {
      await entrar(sessao);
      toast.success('Senha alterada');
      void navegar('/', { replace: true });
    },
    onSettled: () => {
      formulario.reset();
    },
  });

  return (
    <div className="grid min-h-dvh place-items-center bg-fundo px-6 py-12">
      <form
        noValidate
        onSubmit={(evento) => {
          void formulario.handleSubmit((dados) => {
            troca.mutate(dados);
          })(evento);
        }}
        className="grid w-full max-w-sm gap-6"
      >
        <div className="grid gap-2">
          <Marca nome={nomeProduto} />
          <h1 className="text-2xl font-semibold tracking-tight">
            {pendente ? 'Defina sua senha' : 'Trocar senha'}
          </h1>
          <p className="text-base text-texto-suave">
            {pendente
              ? 'Sua senha foi definida pela administração. Escolha uma senha só sua para continuar.'
              : 'Ao trocar, as sessões abertas em outros aparelhos são encerradas.'}
          </p>
        </div>
        <CamposTrocaSenha
          formulario={formulario}
          pendente={pendente}
          email={usuario?.email ?? ''}
        />
        {troca.isError && (
          <p
            role="alert"
            className="rounded-controle bg-perigo-suave px-3 py-2 text-sm text-perigo animate-surgir"
          >
            {mensagemDeErro(troca.error)}
          </p>
        )}
        <div className="grid gap-2">
          <Button type="submit" variante="primario" carregando={troca.isPending} className="w-full">
            <KeyRound aria-hidden />
            Salvar nova senha
          </Button>
          <Button
            variante="fantasma"
            className="w-full"
            onClick={() => {
              if (pendente) void sair();
              else void navegar('/');
            }}
          >
            {pendente ? 'Sair' : 'Cancelar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
